// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/pages/sales/settings/ProgramView.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Loader2, CheckCircle2, XCircle, 
  IndianRupee, Info, Edit, ExternalLink, Globe 
} from 'lucide-react';
import { toast } from 'react-toastify';
import SummaryApi from '../../../../common/SummaryApi';
import api from '../../../../common/apiClient';

const ProgramView = () => {
  const { portalName, id } = useParams();
  const navigate = useNavigate();

  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgramDetails = async () => {
      setLoading(true);
      try {
        const url = typeof SummaryApi.getProgramById.url === 'function'
          ? SummaryApi.getProgramById.url(id)
          : SummaryApi.getProgramById.url;

        const response = await api.get(url);
        if (response.data?.success) {
          setProgram(response.data.data);
        } else {
          toast.error(response.data?.message || 'Failed to load details');
        }
      } catch (error) {
        console.error('Error fetching program details:', error);
        toast.error('Failed to load offering information');
      } finally {
        setLoading(false);
      }
    };

    fetchProgramDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16">
        <Loader2 className="w-8 h-8 text-red-600 animate-spin mb-3" />
        <p className="text-sm text-gray-500">Loading offering details...</p>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="p-8 text-center bg-white rounded-xl shadow-sm border border-gray-200">
        <Info className="w-12 h-12 text-gray-300 mx-auto mb-2" />
        <h3 className="text-base font-bold text-gray-700">Offering not found</h3>
        <button
          onClick={() => navigate(`/${portalName}/sales/settings/programs`)}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition-colors"
        >
          Back to List
        </button>
      </div>
    );
  }

  // ✅ Extract primary thumbnail safely from images[0].url
  const primaryImageUrl = program.images?.[0]?.url || (typeof program.images?.[0] === 'string' ? program.images[0] : null);

  return (
    <div className="p-2 space-y-2.5">
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/${portalName}/sales/settings/programs`)}
            className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
            title="Back to List"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          {/* Primary Cloudinary Image */}
          {primaryImageUrl ? (
            <img
              src={primaryImageUrl}
              alt={program.name}
              className="w-14 h-14 object-cover rounded-xl border border-gray-200 shadow-xs"
            />
          ) : (
            <div className="w-14 h-14 bg-red-50 text-red-600 rounded-xl flex items-center justify-center text-lg font-bold uppercase">
              {program.name?.slice(0, 2)}
            </div>
          )}

          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">{program.name}</h1>
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                program.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {program.isActive ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {program.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-xs text-gray-500 font-mono mt-0.5">
              Code: {program.code || 'N/A'} • Division: <strong className="text-gray-700">{program.divisionId?.name}</strong>
            </p>
          </div>
        </div>

        <button
          onClick={() => navigate(`/${portalName}/sales/settings/programs/edit/${program._id}`)}
          className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium shadow-sm transition-colors"
        >
          <Edit className="w-4 h-4" />
          <span>Edit Offering</span>
        </button>
      </div>

      {/* ✅ Public Website Landing Page Link Banner */}
      {program.websiteRedirectUrl && (
        <div className="bg-blue-50/70 border border-blue-200 p-4 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 text-blue-900 text-sm">
            <Globe className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div>
              <span className="text-xs text-blue-700 block font-medium">Public Website Landing Page:</span>
              <span className="font-semibold underline break-all">{program.websiteRedirectUrl}</span>
            </div>
          </div>
          <a
            href={program.websiteRedirectUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors shadow-xs"
          >
            <span>Visit Live Page</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      )}

      {/* Description & Overview */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">Description</h2>
        <p className="text-sm text-gray-600 leading-relaxed">
          {program.description || 'No description provided for this offering.'}
        </p>

        {/* Specifications Strip */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-gray-100">
          <div>
            <span className="text-xs text-gray-400 block uppercase">Offering Type</span>
            <span className="text-sm font-semibold text-gray-800 capitalize">
              {program.offeringType?.replace('_', ' ')}
            </span>
          </div>

          <div>
            <span className="text-xs text-gray-400 block uppercase">Duration</span>
            <span className="text-sm font-semibold text-gray-800">
              {program.duration?.value ? `${program.duration.value} ${program.duration.unit}` : 'N/A'}
            </span>
          </div>

          <div>
            <span className="text-xs text-gray-400 block uppercase">Total Sessions</span>
            <span className="text-sm font-semibold text-gray-800">
              {program.totalSessions ? `${program.totalSessions} Sessions` : 'N/A'}
            </span>
          </div>

          <div>
            <span className="text-xs text-gray-400 block uppercase">Stock / Inventory</span>
            <span className="text-sm font-semibold text-gray-800">
              {program.stockQuantity !== undefined ? `${program.stockQuantity} in stock` : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Pricing Plans Cards */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">
          Configured Pricing Plans
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {program.pricingPlans?.map((plan, idx) => (
            <div
              key={idx}
              className="p-4 rounded-xl border border-gray-200 bg-gray-50/70 hover:border-red-300 transition-all flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-gray-900 text-base">{plan.planName}</span>
                  <span className="text-[11px] font-semibold uppercase bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                    {plan.billingCycle?.replace('_', ' ')}
                  </span>
                </div>

                {/* Price and Discount */}
                <div className="my-3">
                  {plan.discountType !== 'none' && plan.discountValue > 0 && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-400 line-through">
                        ₹{plan.originalPrice?.toLocaleString('en-IN')}
                      </span>
                      <span className="text-[10px] font-bold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                        {plan.discountType === 'percentage'
                          ? `${plan.discountValue}% OFF`
                          : `₹${plan.discountValue} OFF`}
                      </span>
                    </div>
                  )}

                  <div className="flex items-baseline gap-1 text-2xl font-extrabold text-red-600">
                    <IndianRupee className="w-5 h-5" />
                    <span>{plan.totalAmount?.toLocaleString('en-IN')}</span>
                  </div>
                  <span className="text-[11px] text-gray-400 block mt-0.5">
                    {plan.isTaxInclusive ? 'Inclusive of GST' : 'Exclusive of GST'}
                  </span>
                </div>

                {/* Tax Breakdown */}
                <div className="text-xs space-y-1.5 pt-3 border-t border-gray-200 text-gray-600">
                  <div className="flex justify-between">
                    <span>Base Selling Price:</span>
                    <strong className="text-gray-800">₹{plan.sellingPrice?.toLocaleString('en-IN')}</strong>
                  </div>
                  <div className="flex justify-between">
                    <span>GST ({plan.gstRate}%):</span>
                    <strong className="text-gray-800">₹{plan.taxAmount?.toLocaleString('en-IN')}</strong>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Attributes & Gallery Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Custom Attributes */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
            Custom Attributes
          </h2>
          {program.customFields?.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {program.customFields.map((cf, idx) => (
                <div key={idx} className="py-2.5 flex justify-between text-sm">
                  <span className="text-gray-500 font-medium">{cf.label}</span>
                  <span className="text-gray-900 font-semibold">
                    {typeof cf.value === 'boolean' ? (cf.value ? 'Yes' : 'No') : String(cf.value)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No custom attributes assigned.</p>
          )}
        </div>

        {/* ✅ Fixed: Cloudinary Images Gallery ({ url, publicId } object mapping) */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">
            Images ({program.images?.length || 0})
          </h2>
          {program.images?.length > 0 ? (
            <div className="grid grid-cols-3 gap-2">
              {program.images.map((img, idx) => {
                const imgUrl = img.url || (typeof img === 'string' ? img : '');
                return (
                  <a
                    key={img.publicId || idx}
                    href={imgUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-lg overflow-hidden border border-gray-200 hover:opacity-85 transition-opacity"
                    title="Click to view full image"
                  >
                    <img src={imgUrl} alt={`Images ${idx + 1}`} className="w-full h-24 object-cover" />
                  </a>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">No images uploaded.</p>
          )}
        </div>
      </div>

      {/* Audit Info Footer */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 text-xs text-gray-500 flex flex-wrap items-center justify-between gap-2 shadow-xs">
        <span>Created: {new Date(program.createdAt).toLocaleDateString()}</span>
        <span>Last Updated: {new Date(program.updatedAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
};

export default ProgramView;